// frontend/modules/document-type/views/DocumentTypeManagementView.tsx
import React, { useState, useEffect } from 'react';
import { Card, Button, Space, Typography, Statistic, Row, Col, message } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { DocumentTypeFilters,DocumentTypeList } from '@/modules/document-type';
import { useDocumentTypeStore } from '../stores/document-type-store';
import { useGetAllDocumentTypes } from '@/shared/api/client/document-type-controller';
import { useDocumentTypes } from '../hooks/useDocumentTypes';

const { Title } = Typography;

export const DocumentTypeManagementView: React.FC = () => {
    const getErrorMessage = (error: unknown, fallback: string) => {
        if (error instanceof Error && error.message) {
            return error.message;
        }

        if (typeof error === 'object' && error !== null && 'message' in error) {
            const message = (error as { message?: unknown }).message;
            if (typeof message === 'string' && message.trim()) {
                return message;
            }
        }

        return fallback;
    };

    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const {
        filters,
        setFilters,
        clearFilters,
        stats,
        setStats,
        isLoading,
        setLoading,
        error,
        setError
    } = useDocumentTypeStore();

    const { deleteDocumentType } = useDocumentTypes();

    // Use orval-generated hooks directly
    const { data: paginatedData, isLoading: isLoadingPaginated, error: paginatedError, refetch } = useGetAllDocumentTypes({
        // @ts-ignore
        page: currentPage - 1, // Convert to 0-based indexing
        size: pageSize,
        search: filters.search,
        activeOnly: filters.activeOnly,
        requiredOnly: filters.requiredOnly
    });

    // Get all document types for statistics (use large page size)
    const { data: allDocumentTypesResponse } = useGetAllDocumentTypes({
        // @ts-ignore
        page: 0,
        size: 1000 // Large size to get all for stats
    });

    const allDocumentTypes = allDocumentTypesResponse?.content || [];
    const paginatedDocumentTypes = paginatedData?.content || [];

    useEffect(() => {
        // Calculate stats when all document types are loaded
        if (allDocumentTypes && allDocumentTypes.length > 0) {
            const statsData = {
                total: allDocumentTypes.length,
                active: allDocumentTypes.filter((dt: any) => dt.isActive).length,
                required: allDocumentTypes.filter((dt: any) => dt.isRequired).length,
                withSizeConstraints: allDocumentTypes.filter((dt: any) => 
                    dt.minUploadSizeKb || dt.maxUploadSizeKb
                ).length
            };
            setStats(statsData);
        }
    }, [allDocumentTypes, setStats]);

    const handleFiltersChange = (newFilters: any) => {
        setFilters(newFilters);
        setCurrentPage(1); // Reset to first page when filters change
    };

    const handlePageChange = (page: number, size?: number) => {
        setCurrentPage(page);
        if (size) {
            setPageSize(size);
        }
    };

    const handleEdit = (code: string) => {
        navigate(`/admin/document-types/edit/${code}`);
    };

    const handleView = (code: string) => {
        navigate(`/admin/document-types/view/${code}`);
    };

    const handleDelete = async (code: string) => {
        try {
            await deleteDocumentType.mutateAsync({ code });
            refetch();
        } catch (error) {
            console.error('Failed to delete document type:', error);
        }
    };

    const handleRefresh = () => {
        refetch();
    };

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Title level={2} style={{ margin: 0 }}>
                        Document Type Management
                    </Title>
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={handleRefresh}
                            loading={isLoadingPaginated}
                        >
                            Refresh
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => navigate('/admin/document-types/create')}
                        >
                            Create Document Type
                        </Button>
                    </Space>
                </div>

                {/* Statistics Cards */}
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic
                                title="Total Types"
                                value={stats?.total || 0}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic
                                title="Active Types"
                                value={stats?.active || 0}
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic
                                title="Required Types"
                                value={stats?.required || 0}
                                valueStyle={{ color: '#fa8c16' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic
                                title="With Size Limits"
                                value={stats?.withSizeConstraints || 0}
                                valueStyle={{ color: '#722ed1' }}
                            />
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* Filters */}
            <DocumentTypeFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onClearFilters={clearFilters}
                loading={isLoadingPaginated}
            />

            {/* Document Types List */}
            <DocumentTypeList
                documentTypes={paginatedDocumentTypes}
                loading={isLoadingPaginated}
                error={paginatedError ? getErrorMessage(paginatedError, 'Failed to load document types') : error}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: paginatedData?.totalElements || 0,
                    onChange: handlePageChange
                }}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
            />
        </div>
    );
};
